import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { DndContext, type DragEndEvent, type UniqueIdentifier } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { localizedName } from '@/lib/localized';
import { invalidateCategories } from '@/Redux/Slices/catalogApi';
import { useAppDispatch } from '@/Redux/store';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { useTenantLocales } from '@/Hooks/useTenantLocales';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { LocalizedInput, type LocalizedValue } from '@/components/ui/localized-input';
import {
  DragHandle,
  peerCollision,
  peerKeyboardCoordinates,
  useReorderSensors,
} from '@/components/ui/reorder';
import { type Category, apiError } from './lib';

interface Props {
  /** The selected category; `''` is the "all products" root. */
  selectedId: string;
  onSelect: (id: string) => void;
  /** Raised after any write, so the item form's category picker follows. */
  onChanged: () => void;
  className?: string;
}

const BLANK = {
  /** §25 — one row per locale; the base locale is the category's own `name`. */
  names: {} as LocalizedValue,
  parentId: '',
  sortOrder: '0',
  isActive: true,
};

type Row = { node: Category; depth: number };

/**
 * `GET /categories` carries `_count` and the translation rows; `GET
 * /categories/tree` carries neither, and the counts are the whole point of a
 * tree that sits next to a list. So the parenting is done here instead.
 */
function buildTree(rows: Category[]): Category[] {
  const byId = new Map<string, Category>(rows.map((row) => [row.id, { ...row, children: [] }]));
  const roots: Category[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children!.push(node);
    else roots.push(node);
  }
  return roots;
}

/** Depth-first walk that keeps parents in front of their children. */
function flatten(nodes: Category[], depth = 0): Row[] {
  return nodes.flatMap((node) => [{ node, depth }, ...flatten(node.children ?? [], depth + 1)]);
}

/** Keep a node when it matches, or when any of its descendants do. */
function filterTree(nodes: Category[], matches: (node: Category) => boolean): Category[] {
  return nodes.flatMap((node) => {
    const kept = filterTree(node.children ?? [], matches);
    if (matches(node)) return [node];
    return kept.length ? [{ ...node, children: kept }] : [];
  });
}

function subtreeIds(node: Category): string[] {
  return [node.id, ...(node.children ?? []).flatMap(subtreeIds)];
}

/**
 * One `<li>` of the tree, wired for dragging.
 *
 * A component rather than a branch inside the map because `useSortable` is a
 * hook and a row is where it belongs. The row's own markup stays in the tree
 * below, handed back through `children` with the handle to place at its start —
 * so the drag wiring lives here and the row's layout, selection and action
 * strip stay exactly where they were.
 *
 * The indent is `paddingInlineStart`, not `padding-left`: the tree nests toward
 * the reading direction, so an Arabic catalogue indents from the right.
 */
function SortableCategoryRow({
  id,
  depth,
  draggable,
  handleLabel,
  children,
}: {
  id: string;
  depth: number;
  draggable: boolean;
  handleLabel: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable });

  return (
    <li
      ref={setNodeRef}
      style={{
        paddingInlineStart: `${depth * 14}px`,
        // `Translate`, not `Transform`: scaling a row would smear its text.
        transform: CSS.Translate.toString(transform),
        transition,
        // Lifted above the rows it passes, or it slides underneath them.
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
      }}
      className={cn(isDragging && 'opacity-60')}
    >
      {children(
        draggable ? (
          <DragHandle
            ref={setActivatorNodeRef}
            label={handleLabel}
            dragging={isDragging}
            {...attributes}
            {...listeners}
          />
        ) : (
          // Reserved even when dragging is off, so turning the filter on does
          // not shuffle every name sideways.
          <span className="w-8 shrink-0" aria-hidden />
        ),
      )}
    </li>
  );
}

/**
 * The category tree that navigates the item list beside it.
 *
 * Deliberately not a tree *of items*. The venue this was built for runs 122
 * products across 16 categories and the biggest holds 32, so expanding a
 * category only hands the flat list back one level in — while collapsing hides
 * things at the exact moment someone is hunting for them. The tree is
 * navigation and the list stays the work surface: pick a node, the list beside
 * it filters, and items stay flat, searchable and bulk-editable.
 *
 * Category writes are `menu.create` / `menu.update` / `menu.delete`, matching
 * `CategoriesController`.
 */
export default function CategoryTree({ selectedId, onSelect, onChanged, className }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const dispatch = useAppDispatch();
  const { locales, defaultLocale } = useTenantLocales();
  const canCreate = can('menu.create');
  const canUpdate = can('menu.update');
  const canDelete = can('menu.delete');

  const [tree, setTree] = useState<Category[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  /** Narrow screens open the panel on demand; `lg:` keeps it open regardless. */
  const [open, setOpen] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const label = useCallback(
    (node: Category) => localizedName(node, i18n.language),
    [i18n.language],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Category[] }>('/categories', { params: { limit: 200 } })
      .then(({ data }) => {
        setTree(buildTree(data.data ?? []));
        setDirty(false);
      })
      .catch((e) => setError(apiError(e, t('catalog.categories.loadFailed'))))
      .finally(() => setLoading(false));
    // The root's count is the tenant's whole catalogue, which no category row
    // knows; one page of one item is the cheapest way to ask for `meta.total`.
    api
      .get<{ meta?: { total: number } }>('/catalog', { params: { limit: 1 } })
      .then(({ data }) => setTotal(data.meta?.total ?? null))
      .catch(() => setTotal(null));
  }, [t]);

  useEffect(load, [load]);

  const flat = useMemo(() => flatten(tree), [tree]);
  const selected = useMemo(
    () => flat.find(({ node }) => node.id === selectedId)?.node ?? null,
    [flat, selectedId],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return tree;
    return filterTree(tree, (node) => label(node).toLowerCase().includes(needle));
  }, [tree, query, label]);

  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  // --- reordering ------------------------------------------------------------

  const searching = query.trim().length > 0;

  /**
   * Dragging is off while the filter box has text.
   *
   * A filtered tree hides siblings, and dropping "between" two rows that have a
   * hidden row between them writes an order the operator never saw. Refusing to
   * drag is the honest version of that; the filter is for finding a category,
   * not for ordering one.
   */
  const canDrag = canUpdate && !searching;

  const nodeById = useMemo(() => {
    const map = new Map<string, Category>();
    const walk = (nodes: Category[]) => {
      for (const node of nodes) {
        map.set(node.id, node);
        walk(node.children ?? []);
      }
    };
    walk(tree);
    return map;
  }, [tree]);

  /**
   * A drop is legal only between rows that share a parent.
   *
   * `ReorderCategoriesDto` carries `{ id, sortOrder }` and nothing else, so the
   * one write this flow makes cannot express a new parent. Re-parenting by drag
   * would therefore have to be a second, immediate `PATCH /categories/:id` —
   * which would mean a drag that silently writes to the server while the
   * "Save order" button beside it still claims nothing has been saved. Order is
   * deferred and re-parenting stays where it already is: the category's own
   * form, which has a parent picker and gets `assertNoCycle` checked for free.
   */
  const isPeer = useCallback(
    (active: UniqueIdentifier, candidate: UniqueIdentifier) =>
      (nodeById.get(String(active))?.parentId ?? '') ===
      (nodeById.get(String(candidate))?.parentId ?? ''),
    [nodeById],
  );

  const sensors = useReorderSensors(useMemo(() => peerKeyboardCoordinates(isPeer), [isPeer]));
  const collisionDetection = useMemo(() => peerCollision(isPeer), [isPeer]);

  /** Where a row sits among its own siblings — what an announcement has to say. */
  const positionOf = useCallback(
    (id: UniqueIdentifier) => {
      const node = nodeById.get(String(id));
      const siblings = node?.parentId ? (nodeById.get(node.parentId)?.children ?? []) : tree;
      return {
        name: node ? label(node) : String(id),
        index: siblings.findIndex((n) => n.id === String(id)) + 1,
        count: siblings.length,
      };
    },
    [nodeById, tree, label],
  );

  /**
   * What a screen reader hears. dnd-kit's defaults are English strings baked
   * into the library, which on an Arabic dashboard would be the one part of the
   * screen that never translates.
   */
  const accessibility = useMemo(
    () => ({
      screenReaderInstructions: { draggable: t('catalog.reorder.instructions') },
      announcements: {
        onDragStart: ({ active }: { active: { id: UniqueIdentifier } }) =>
          t('catalog.reorder.picked', positionOf(active.id)),
        onDragOver: ({ over }: { over: { id: UniqueIdentifier } | null }) =>
          over ? t('catalog.reorder.movedOver', positionOf(over.id)) : undefined,
        onDragEnd: ({ active }: { active: { id: UniqueIdentifier } }) =>
          t('catalog.reorder.dropped', positionOf(active.id)),
        onDragCancel: ({ active }: { active: { id: UniqueIdentifier } }) =>
          t('catalog.reorder.cancelled', positionOf(active.id)),
      },
    }),
    [t, positionOf],
  );

  /** Move a row within its own sibling list and renumber that list. */
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !isPeer(active.id, over.id)) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    const reorder = (nodes: Category[]): Category[] => {
      const from = nodes.findIndex((n) => n.id === activeId);
      const to = nodes.findIndex((n) => n.id === overId);
      if (from >= 0 && to >= 0) {
        return arrayMove(nodes, from, to).map((n, i) => ({ ...n, sortOrder: i }));
      }
      return nodes.map((n) => ({ ...n, children: reorder(n.children ?? []) }));
    };

    setTree(reorder(tree));
    setDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const items = flatten(tree).map(({ node }) => ({ id: node.id, sortOrder: node.sortOrder }));
      await api.post('/categories/reorder', { items });
      toast.success(t('catalog.categories.orderSaved'));
      load();
      dispatch(invalidateCategories());
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.categories.saveFailed')));
    } finally {
      setSavingOrder(false);
    }
  };

  const openCreate = (parentId = '') => {
    setEditing(null);
    setForm({ ...BLANK, parentId });
    setFormOpen(true);
  };

  const openEdit = (category: Category) => {
    setEditing(category);
    const names: LocalizedValue = { [defaultLocale]: category.name };
    for (const row of category.translations ?? []) names[row.locale] = row.name;
    setForm({
      names,
      parentId: category.parentId ?? '',
      sortOrder: String(category.sortOrder),
      isActive: category.isActive,
    });
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    // Falls back to whichever language was filled, so a category is never nameless.
    const baseName =
      form.names[defaultLocale]?.trim() ||
      locales.map((code) => form.names[code]?.trim()).find(Boolean) ||
      '';
    const body = {
      name: baseName,
      parentId: form.parentId || undefined,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
    };
    try {
      const saved = editing
        ? ((await api.patch(`/categories/${editing.id}`, body)).data as { data?: { id: string } })
        : ((await api.post('/categories', body)).data as { data?: { id: string } });
      const categoryId = editing?.id ?? saved?.data?.id;

      // §25 — every non-empty locale becomes a row on the same category.
      const translations = locales
        .filter((code) => form.names[code]?.trim())
        .map((code) => ({ locale: code, name: form.names[code].trim() }));
      if (categoryId && translations.length) {
        await api.put(`/categories/${categoryId}/translations`, { translations });
      }
      toast.success(t(editing ? 'catalog.categories.updated' : 'catalog.categories.created'));
      setFormOpen(false);
      // A new child is worth landing on; a rename should not move the operator.
      if (!editing && categoryId) onSelect(categoryId);
      load();
      dispatch(invalidateCategories());
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.categories.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  /**
   * `CategoriesService.remove` refuses while children or items still point at
   * the category. Mirroring the guard here is not enough on its own — a
   * greyed-out button says nothing about *why* — so the reason is what the
   * operator gets, naming the count that is in the way.
   */
  const deleteBlockedBy = (node: Category): string | null => {
    const children = node.children?.length ?? node._count?.children ?? 0;
    if (children) return t('catalog.categories.deleteBlockedChildren', { count: children });
    const items = node._count?.items ?? 0;
    if (items) return t('catalog.categories.deleteBlockedItems', { count: items });
    return null;
  };

  const remove = async (node: Category) => {
    const blocked = deleteBlockedBy(node);
    if (blocked) {
      toast.error(blocked);
      return;
    }
    const confirmed = await confirm({
      title: t('catalog.categories.deleteConfirm', { name: label(node) }),
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/categories/${node.id}`);
      toast.success(t('catalog.categories.deleted'));
      if (selectedId === node.id) onSelect('');
      load();
      dispatch(invalidateCategories());
      onChanged();
    } catch (e) {
      // The server is still the authority — a race can beat the local guard.
      toast.error(apiError(e, t('catalog.categories.saveFailed')));
    }
  };

  /** A category cannot be re-parented into its own subtree (`assertNoCycle`). */
  const parentOptions = editing
    ? flat.filter(({ node }) => !subtreeIds(editing).includes(node.id))
    : flat;

  const selectedLabel = selected ? label(selected) : t('catalog.categories.allProducts');

  /**
   * The row's actions, as icons, sitting on the row itself.
   *
   * Hidden until the row is hovered, focused or selected — but with
   * `visibility`, not `opacity`. An `opacity-0` button is still under the
   * finger and still in the tab order, which on a till is a Delete nobody can
   * see; `visibility` takes it out of hit-testing and out of the tab order,
   * while still reserving nothing, so the row never reflows as it appears.
   *
   * **Hover is not an interaction a till or a tablet has**, so it is never the
   * only way in. The strip is permanently on for the *selected* row, and a tap
   * is what selects — the same tap-only route the floor map's table menu
   * settled on. Keyboard reaches it through `group-focus-within`: Tab lands on
   * the row's name button, which is always visible, and that reveals the rest.
   * Tailwind 4 already scopes its `hover` variants to `@media (hover: hover)`,
   * so the hover clause cannot fire a phantom reveal on a touchscreen.
   *
   * `bg-inherit` keeps the strip on the row's own background, so it covers the
   * tail of a long name rather than squeezing the name into nothing.
   */
  const rowActions = (node: Category, isSelected: boolean) => {
    const blocked = deleteBlockedBy(node);
    // Full row height: 44px of finger, even though the icon is 16px.
    const iconButton = 'h-11 w-8 shrink-0 px-0';
    return (
      <div
        className={cn(
          'absolute inset-y-0 end-1 flex items-center gap-0.5 bg-inherit ps-2',
          isSelected ? 'visible' : 'invisible group-hover:visible group-focus-within:visible',
        )}
      >
        {canCreate && (
          <Button
            size="sm"
            variant="ghost"
            className={iconButton}
            onClick={() => openCreate(node.id)}
            aria-label={t('catalog.categories.addChild')}
            title={t('catalog.categories.addChild')}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
        {canUpdate && (
          <Button
            size="sm"
            variant="ghost"
            className={iconButton}
            onClick={() => openEdit(node)}
            aria-label={t('common.edit')}
            title={t('common.edit')}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {canDelete && (
          // Destructive, but a red icon rather than a red slab — it should read
          // as different, not as the loudest thing in the panel. When the
          // server would refuse, the reason is already on the tooltip and the
          // click says it again out loud.
          <Button
            size="sm"
            variant="ghost"
            className={cn(iconButton, 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 dark:bg-red-950/50 hover:text-red-700 dark:text-red-400')}
            onClick={() => remove(node)}
            aria-label={t('common.delete')}
            title={blocked ?? t('common.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  /**
   * One level of the tree, and every level below it.
   *
   * Each sibling list gets its own `SortableContext`, which is what keeps a
   * drag honest in a nested tree: the gap opens between siblings, and the rows
   * of some other parent that happen to sit between them on screen stay put. A
   * `SortableContext` renders no DOM of its own, so the `<ul>` still contains
   * nothing but `<li>` — the nesting is in React, not in the markup.
   */
  const renderLevel = (nodes: Category[], depth: number): ReactNode => (
    <SortableContext items={nodes.map((node) => node.id)} strategy={verticalListSortingStrategy}>
      {nodes.map((node) => {
        const hasChildren = (node.children ?? []).length > 0;
        const isSelected = node.id === selectedId;
        // A filter that hid its own matches behind a collapsed parent would be
        // worse than no filter, so searching expands everything it kept.
        const showChildren = hasChildren && (searching || !collapsed.has(node.id));
        return (
          <Fragment key={node.id}>
            <SortableCategoryRow
              id={node.id}
              depth={depth}
              draggable={canDrag}
              handleLabel={t('catalog.categories.dragHandle', { name: label(node) })}
            >
              {(handle) => (
                <div
                  className={cn(
                    'group relative flex min-h-11 items-center gap-1 rounded-lg pe-1',
                    isSelected
                      ? 'bg-accent-soft text-accent-soft-fg'
                      : 'text-ink hover:bg-hover focus-within:bg-hover',
                  )}
                  /* On the row rather than on the count itself: the
                     action strip sits over the count on a selected row. */
                  title={t('catalog.categories.counts', {
                    items: node._count?.items ?? 0,
                    children: (node.children ?? []).length,
                  })}
                >
                  {handle}
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() => toggle(node.id)}
                      aria-label={t(
                        collapsed.has(node.id)
                          ? 'catalog.categories.expand'
                          : 'catalog.categories.collapse',
                      )}
                      aria-expanded={!collapsed.has(node.id)}
                      className="flex h-11 w-7 shrink-0 items-center justify-center"
                    >
                      {collapsed.has(node.id) ? (
                        <ChevronRight className="h-4 w-4 rtl:-scale-x-100" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  ) : (
                    <span className="w-7 shrink-0" aria-hidden />
                  )}
                  <button
                    type="button"
                    onClick={() => onSelect(node.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 py-2 text-start text-sm"
                  >
                    {/* The name may be Arabic while the dashboard is in
                        English — `dir="auto"` keeps it readable either way. */}
                    <span
                      className={cn('truncate', isSelected && 'font-medium')}
                      dir="auto"
                      title={label(node)}
                    >
                      {label(node)}
                    </span>
                    {!node.isActive && (
                      <Badge tone="neutral" className="shrink-0">
                        {t('catalog.items.inactive')}
                      </Badge>
                    )}
                    {/* The item count is the number worth seeing at a
                        glance; the sub-category count rides along on the
                        row's tooltip rather than costing a second line. */}
                    <span className="ms-auto shrink-0 text-xs tabular-nums text-ink-muted">
                      {node._count?.items ?? 0}
                    </span>
                  </button>
                  {rowActions(node, isSelected)}
                </div>
              )}
            </SortableCategoryRow>
            {showChildren ? renderLevel(node.children ?? [], depth + 1) : null}
          </Fragment>
        );
      })}
    </SortableContext>
  );

  return (
    <Card className={cn('overflow-hidden', className)}>
      <div className="flex items-center gap-2 border-b p-3">
        {/* On a tablet the tree is a disclosure that names the current filter,
            so a collapsed panel still tells the operator what the list shows. */}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-start lg:pointer-events-none"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 lg:hidden" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 lg:hidden rtl:-scale-x-100" />
          )}
          <span className="truncate text-sm font-semibold">{t('catalog.tabs.categories')}</span>
          <span className="truncate text-xs text-ink-muted lg:hidden" dir="auto">
            {selectedLabel}
          </span>
        </button>
        {canCreate && (
          <Button size="sm" variant="outline" onClick={() => openCreate()}>
            {t('catalog.categories.newCategory')}
          </Button>
        )}
      </div>

      <div className={cn(open ? 'block' : 'hidden', 'lg:block')}>
        <div className="border-b p-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('catalog.categories.filterPlaceholder')}
            aria-label={t('catalog.categories.filterPlaceholder')}
          />
        </div>

        {loading ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : (
          <div className="max-h-[24rem] overflow-y-auto lg:max-h-[calc(100vh-20rem)]">
            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetection}
              // A category only ever moves up or down its own list, so the
              // pointer's horizontal wander is discarded rather than rendered.
              modifiers={[restrictToVerticalAxis]}
              accessibility={accessibility}
              onDragEnd={onDragEnd}
            >
              <ul className="p-1">
                <li>
                  <button
                    type="button"
                    onClick={() => onSelect('')}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-2 rounded-lg px-2 text-start text-sm',
                      selectedId === ''
                        ? 'bg-accent-soft font-medium text-accent-soft-fg'
                        : 'hover:bg-hover',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {t('catalog.categories.allProducts')}
                    </span>
                    {total !== null && (
                      <span className="shrink-0 text-xs tabular-nums text-ink-muted">
                        {total}
                      </span>
                    )}
                  </button>
                </li>

                {filtered.length === 0 ? (
                  <li className="p-2">
                    {searching ? (
                      <p className="text-sm text-ink-muted">
                        {t('catalog.categories.noMatches')}
                      </p>
                    ) : (
                      <EmptyState
                        title={t('catalog.categories.empty')}
                        hint={t('catalog.categories.emptyHint')}
                      />
                    )}
                  </li>
                ) : (
                  renderLevel(filtered, 0)
                )}
              </ul>
            </DndContext>
          </div>
        )}

        {canUpdate && dirty ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t p-3">
            <p className="text-xs text-ink-muted">
              {t('catalog.categories.unsavedOrder')}
            </p>
            <div className="flex items-center gap-2">
              {/* Dragging is easier to do by accident than clicking an arrow
                  was, so the way back is next to the way forward. */}
              <Button size="sm" variant="ghost" disabled={savingOrder} onClick={load}>
                {t('catalog.categories.discardOrder')}
              </Button>
              <Button size="sm" variant="outline" disabled={savingOrder} onClick={saveOrder}>
                {t('catalog.categories.saveOrder')}
              </Button>
            </div>
          </div>
        ) : (
          canDrag &&
          filtered.length > 1 && (
            <p className="border-t p-3 text-xs text-ink-muted">
              {t('catalog.categories.hint')}
            </p>
          )
        )}
        {canUpdate && searching && (
          <p className="border-t p-3 text-xs text-ink-muted">
            {t('catalog.categories.reorderFilterOff')}
          </p>
        )}
      </div>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'catalog.categories.editCategory' : 'catalog.categories.newCategory')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={saving || !Object.values(form.names).some((value) => value.trim())}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('catalog.items.name')} required>
            <LocalizedInput
              value={form.names}
              onChange={(names) => setForm({ ...form, names })}
              locales={locales}
              autoFocus
            />
          </Field>
          <Field label={t('catalog.categories.parent')} hint={t('catalog.categories.parentHint')}>
            <Select
              value={form.parentId}
              onChange={(e) => setForm({ ...form, parentId: e.target.value })}
            >
              <option value="">{t('catalog.categories.root')}</option>
              {parentOptions.map(({ node, depth }) => (
                <option key={node.id} value={node.id}>
                  {'— '.repeat(depth)}
                  {label(node)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('catalog.items.sortOrder')}>
            <Input
              type="number"
              min="0"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            {t('catalog.items.active')}
          </label>
        </div>
      </Dialog>
    </Card>
  );
}
